import { Checkbox, Radio, RadioChangeEvent, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { BsInfoCircle } from 'react-icons/bs';
import { FaLinkedin } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { MdOutlineEmail, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { RiKey2Line } from 'react-icons/ri';
import { VscReferences } from 'react-icons/vsc';
import { useDispatch } from 'react-redux';
import Apis from '../../api';
import usePageRendered from '../../hooks/usePageRendered';
import HSButton from '../common/Button';
import InputToolTip from '../common/InputTooltip';
import NormalInput from '../common/NormalInput';
import { socialLinkedinRedurectURL, socialRedirectURL } from '../login/Login';
import actions from '../login/redux/actions';
import { Investor, User, UserState } from '../login/redux/types';
import messageActions from '../message/redux/actions';
import { federatedActions } from '../socialLogin';
import { checkPasswordStrength, emailValidation } from '../utils/validation';

type SignUpResponse = {
    uuid?: string;
    jwtToken?: string;
    user?: User;
    investor?: Investor;
};

type Props = {
    referralError?: string;
    referral_code?: string;
};

const SignUp: React.FC<Props> = ({ referralError, referral_code }) => {
    const pageTransitionCss = usePageRendered();
    const dispatch = useDispatch();

    const [formFields, setFormFields] = useState<FormField>({
        email: '',
        password: '',
        confirmPassword: '',
        isCheck: false,
        isValidate: false,
        accreditedInvestor: '',
    });
    const [passwordStrength, setPasswordStrength] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [isVisibility, setVisibility] = useState<boolean>(false);
    const [isVisibilityR, setVisibilityR] = useState<boolean>(false);

    const form = useMemo(() => {
        const colorCondition = `${passwordStrength === 0 ? '#eb5757' : passwordStrength === 1 ? '#f1e915' : '#3fb6b2'}`;
        const onFieldChange = (data: { name: string; value?: number | string }) => {
            const { name, value } = data;
            let obj = { ...formFields };
            if (name === 'password') {
                setPasswordStrength(checkPasswordStrength(String(value)));
            }
            let v = name === 'email' ? (value ? String(value).toLowerCase() : value) : value;
            if (name) {
                obj[name] = (v as string) || '';
            }
            setFormFields(obj);
        };

        const onCheckboxChange = (event: any) => {
            const name = event.target.name;
            let obj = { ...formFields };
            obj[name] = event.target.checked;
            setFormFields(obj);
        };

        const onChangeRadio = (e: RadioChangeEvent) => {
            const name = e.target.name || 'accreditedInvestor';
            let obj = { ...formFields };
            obj[name] = e.target.value;
            setFormFields(obj);
        };
        return (
            <>
                <div className="border border-bottom-0">
                    <InputToolTip title="Email">
                        <NormalInput
                            placeholder="Email"
                            value={formFields.email}
                            name="email"
                            isRequired
                            onChange={onFieldChange}
                            error={(formFields.isValidate as boolean) && !emailValidation(String(formFields.email))}
                            prefix={<MdOutlineEmail color="rgb(196, 196, 196)" size={'1rem'} />}
                            bordered={false}
                        />
                        {formFields.isValidate && !emailValidation(String(formFields.email)) && (
                            <div className="common_error px-3">Please enter valid email</div>
                        )}
                    </InputToolTip>
                </div>
                <div className="border border-bottom-0">
                    <InputToolTip title="Password">
                        <NormalInput
                            placeholder="Password"
                            value={formFields.password}
                            name="password"
                            type={isVisibility ? 'text' : 'password'}
                            isRequired
                            onChange={onFieldChange}
                            error={(formFields.isValidate as boolean) && formFields.password.trim() === ''}
                            prefix={<RiKey2Line color="rgb(196, 196, 196)" size={'1rem'} />}
                            bordered={false}
                            suffix={
                                isVisibility ? (
                                    <MdVisibility
                                        className="cursor-pointer"
                                        size="1.2rem"
                                        onClick={() => setVisibility(!isVisibility)}
                                    />
                                ) : (
                                    <MdVisibilityOff
                                        className="cursor-pointer"
                                        size="1.2rem"
                                        onClick={() => setVisibility(!isVisibility)}
                                    />
                                )
                            }
                        />
                        {passwordStrength !== -1 && (
                            <div className="px-3">
                                <div>
                                    <div className="pwd-str-box" style={{ backgroundColor: colorCondition }} />
                                    <div
                                        className="pwd-str-box"
                                        style={passwordStrength > 0 ? { backgroundColor: colorCondition } : {}}
                                    />
                                    <div
                                        className="pwd-str-box"
                                        style={passwordStrength > 1 ? { backgroundColor: colorCondition } : {}}
                                    />
                                </div>
                                <span
                                    style={passwordStrength === 0 ? { color: colorCondition } : { color: 'black' }}
                                    className="text-[12px]"
                                >
                                    {passwordStrength <= 1
                                        ? `We highly recommend that your password includes at least one number, letter, and special character.`
                                        : ``}
                                </span>
                            </div>
                        )}
                        {formFields.isValidate && formFields.password.trim() === '' && (
                            <div className="common_error px-3">Please enter password</div>
                        )}
                    </InputToolTip>
                </div>
                <div className={`border ${referral_code ? 'border-bottom-0' : ''}`}>
                    <InputToolTip title="Repeat Password">
                        <NormalInput
                            placeholder="Repeat Password"
                            value={formFields.confirmPassword}
                            name="confirmPassword"
                            type={isVisibilityR ? 'text' : 'password'}
                            isRequired
                            onChange={onFieldChange}
                            error={
                                (formFields.isValidate as boolean) &&
                                (formFields.confirmPassword.trim() === '' ||
                                    formFields.password !== formFields.confirmPassword)
                            }
                            prefix={<RiKey2Line color="rgb(196, 196, 196)" size={'1rem'} />}
                            bordered={false}
                            suffix={
                                isVisibilityR ? (
                                    <MdVisibility
                                        className="cursor-pointer"
                                        size="1.2rem"
                                        onClick={() => setVisibilityR(!isVisibilityR)}
                                    />
                                ) : (
                                    <MdVisibilityOff
                                        className="cursor-pointer"
                                        size="1.2rem"
                                        onClick={() => setVisibilityR(!isVisibilityR)}
                                    />
                                )
                            }
                        />
                        {formFields.isValidate && formFields.confirmPassword.trim() === '' && (
                            <div className="common_error px-3">Please enter repeat password</div>
                        )}
                        {formFields.isValidate &&
                            formFields.confirmPassword &&
                            formFields.confirmPassword.trim() !== '' &&
                            formFields.password !== formFields.confirmPassword && (
                                <div className="common_error px-3">
                                    {' '}
                                    Password don't match, Please enter correct password
                                </div>
                            )}
                    </InputToolTip>
                </div>
                {referral_code && (
                    <div className="border">
                        <InputToolTip title="Referral Code" isShow={true}>
                            <NormalInput
                                placeholder="Referral Code"
                                value={referral_code}
                                disabled={true}
                                name="referral_code"
                                type="text"
                                onChange={() => null}
                                bordered={false}
                                prefix={<VscReferences color="rgb(196, 196, 196)" size={'1rem'} />}
                            />

                            {referralError && <div className="common_error color-gold px-3">{referralError}</div>}
                        </InputToolTip>
                    </div>
                )}
                <div className="mt-4 text-color-404040">
                    <p className="flex items-center text-[#666666] font-[Lato] text-sm">
                        Are you an accredited or institutional investor? <span className="common_error">*</span>{' '}
                        <Tooltip
                            overlayInnerStyle={{ backgroundColor: '#fff', color: '#404040' }}
                            overlayStyle={{ width: '500px', maxWidth: '90%' }}
                            overlayClassName="custome_tooltip"
                            color="#fff"
                            title={
                                <div>
                                    <p className="font-semibold text-sm">Accredited Investor:</p>
                                    <div>
                                        <p className="font-semibold text-sm">(i) Individual Investor:</p>
                                        <ul className="list-disc pl-3 text-xs text-justify">
                                            <li>
                                                Individual with personal net assets of at least S$2 million (or
                                                equivalent)
                                            </li>
                                            <li>
                                                Individual with financial assets (net of any related liabilities)
                                                exceeding S$1 million in value (or its equivalent in a foreign currency)
                                            </li>
                                            <li>
                                                Individual with total income of at least S$300,000 (or equivalent) for
                                                the last 12 months
                                            </li>
                                        </ul>
                                        <p className="font-semibold text-sm">(ii) Corporate Investor:</p>
                                        <ul className="list-disc pl-3 text-xs text-justify">
                                            <li>
                                                Represents a corporation with net assets exceeding S$10 million (or
                                                equivalent)
                                            </li>
                                            <li>
                                                Represents a corporation where the entire share capital is owned by one
                                                or more persons, all of whom are accredited investors
                                            </li>
                                        </ul>
                                    </div>
                                    <p className="font-semibold text-sm mt-1">Institutional Investor:</p>
                                    <span className="text-xs relative -top-1.5">(including but not limited to)</span>
                                    <ul className="list-disc pl-3 text-xs text-justify relative -top-1">
                                        <li>A corporation that holds a capital markets services licence</li>
                                        <li>
                                            An entity that is wholly and beneficially owned by the central government of
                                            a country
                                        </li>
                                        <li>A bank that is licensed under the Banking Act</li>
                                    </ul>
                                </div>
                            }
                        >
                            <BsInfoCircle className="ml-1 text-blue-500" />
                        </Tooltip>
                    </p>
                    <div>
                        <Radio.Group
                            onChange={onChangeRadio}
                            value={formFields.accreditedInvestor}
                            name="accreditedInvestor"
                            className="radio_custome"
                        >
                            <Radio value={'Yes'} className="text-sm text-[#666666] font-[WorkSans]">
                                Yes
                            </Radio>
                            <Radio value={'No'} className="text-sm text-[#666666] font-[WorkSans]">
                                No
                            </Radio>
                        </Radio.Group>
                    </div>
                    {formFields.accreditedInvestor === 'No' && (
                        <span className="common_error">
                            The Helicap platform is only open to accredited investors at this time
                        </span>
                    )}
                </div>
                <div className="mt-4 flex">
                    <Checkbox checked={formFields.isCheck} onChange={onCheckboxChange} name="isCheck" />
                    <TermAndConditionOnLanding />
                </div>
            </>
        );
    }, [formFields, passwordStrength, referralError, referral_code, isVisibility, isVisibilityR]);

    const onClickSignUp = () => {
        setFormFields({ ...formFields, isValidate: true });
        if (
            !emailValidation(String(formFields.email)) ||
            formFields.password.trim() === '' ||
            formFields.password !== formFields.confirmPassword ||
            formFields.accreditedInvestor !== 'Yes' ||
            !formFields.isCheck
        ) {
            return;
        }
        setFormFields({ ...formFields, isValidate: false });
        setLoading(true);
        Apis.signUp({
            email: formFields.email.trim(),
            password: formFields.password,
            referralCode: referral_code ? referral_code : '',
        })
            .then((res) => {
                const response: SignUpResponse = res.data;
                const { jwtToken, user, investor } = response;
                const userData: UserState = {
                    jwtToken,
                    user,
                    investor,
                    uuid: user?.uuid,
                };
                dispatch(actions.loginReq(userData, undefined));
            })
            .catch((err) => {
                dispatch(messageActions.showMessage({ error: true, text: String(err) }));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const socialLoginStyle = useMemo(() => {
        return {
            borderRadius: 99999,
            paddingRight: '2rem',
            paddingLeft: '2rem',
            backgroundColor: !formFields.isCheck || formFields.accreditedInvestor !== 'Yes' ? '#f5f5f5' : '#fff',
            color: !formFields.isCheck || formFields.accreditedInvestor !== 'Yes' ? 'rgba(0, 0, 0, 0.25)' : '#20222B',
            borderColor: !formFields.isCheck || formFields.accreditedInvestor !== 'Yes' ? '#d9d9d9' : '#20222B',
        };
    }, [formFields.isCheck, formFields.accreditedInvestor]);

    return (
        <div className="flex justify-center items-center">
            <div className="w-full" id="signUp">
                <div className={pageTransitionCss}>{form}</div>
                <div className="flex justify-center items-center m-2 pt-6">
                    <HSButton
                        title="Sign Up"
                        disabled={!formFields.isCheck || formFields.accreditedInvestor !== 'Yes'}
                        loading={loading}
                        onClick={onClickSignUp}
                        style={{ borderRadius: 0, paddingRight: '2rem', paddingLeft: '2rem' }}
                    />
                </div>
                {!referral_code && process.env.REACT_APP_SOCAIL_LOGIN_ENABLE === 'yes' && (
                    <>
                        <div className="relative flex py-5 items-center">
                            <div className="flex-grow border-t border-[#C4CFE4]"></div>
                            <span className="flex-shrink mx-4 text-[#404040] font-[WorkSans]">OR</span>
                            <div className="flex-grow border-t border-[#C4CFE4]"></div>
                        </div>
                        <div className="flex justify-center items-center m-4 mt-2 mb-4 flex-col social_login">
                            <>
                                <HSButton
                                    title="Sign Up with Google"
                                    light
                                    icon={<FcGoogle />}
                                    disabled={!formFields.isCheck || formFields.accreditedInvestor !== 'Yes'}
                                    onClick={() => {
                                        window.open(
                                            `${process.env.REACT_APP_BACKEND_URL}${socialRedirectURL}?action=${federatedActions.signup}`,
                                            '_self',
                                        );
                                    }}
                                    style={{ ...socialLoginStyle }}
                                />

                                <HSButton
                                    title="Sign Up with Linkedin"
                                    light
                                    disabled={!formFields.isCheck || formFields.accreditedInvestor !== 'Yes'}
                                    icon={<FaLinkedin color="#2464ad" />}
                                    onClick={() => {
                                        window.open(
                                            `${process.env.REACT_APP_BACKEND_URL}${socialLinkedinRedurectURL}?action=${federatedActions.signup}`,
                                            '_self',
                                        );
                                    }}
                                    style={{ ...socialLoginStyle, marginTop: '1rem' }}
                                />
                            </>
                        </div>
                    </>
                )}
                {/* <p className="flex text-base justify-center items-center">
                    Already have an account?
                    <HSButton
                        title="Log in"
                        type="link"
                        onClick={() => history.push(HEADERMENU_PATH.login)}
                        className="text-base text-blue-500 pl-0"
                    />
                </p> */}
                {referral_code && (
                    <p className="block md:hidden w-full text-center text-base text-black py-8">
                        <b>Not ready to sign-up yet?</b>
                        <br />
                        Leave us your contact{' '}
                        <a
                            className="underline text-blue-500"
                            href="https://meetings.hubspot.com/joyce-liu"
                            rel="noreferrer"
                            target={'_blank'}
                        >
                            here
                        </a>{' '}
                        for a quick 5 minute introductory call
                    </p>
                )}
            </div>
        </div>
    );
};

export default SignUp;

interface FormField {
    [k: string]: string | boolean;
    email: string;
    password: string;
    confirmPassword: string;
    isCheck: boolean;
    accreditedInvestor: 'Yes' | 'No' | '';
}

export const TermAndConditionOnLanding = () => {
    return (
        <span className="text-justify ml-2 text-xs text-[#666666] font-[Lato]">
            By proceeding, I agree that Helicap Securities or its representatives may contact me by email, phone, or SMS
            (including by automated means) at the email address or number I provide. I have read and understood the{' '}
            <a
                href={`${process.env.REACT_APP_LANDING_WEB_URL}/terms-of-service`}
                rel="noreferrer"
                target={'_blank'}
                className="text-[#1B3E8C] font-[Lato-Semibold] font-semibold"
            >
                Terms of Service
            </a>{' '}
            and{' '}
            <a
                href={`${process.env.REACT_APP_LANDING_WEB_URL}/privacy-policy`}
                rel="noreferrer"
                target={'_blank'}
                className="text-[#1B3E8C] font-[Lato-Semibold] font-semibold"
            >
                Privacy Statement
            </a>
            .
        </span>
    );
};
